import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import ProfileSquelette from "../layouts/ProfileSquelette";
import Person from "../components/Person";
import Spinner from "../components/Spinner";
import { AuthContext } from "../contexts/AuthContext";
import { WorkspaceContext } from "../contexts/WorkspaceContext";
import { DisplayDiscussionForMiniScreenProvider } from "../contexts/DisplayDiscussionForMiniScreenContext";
import { socket } from "../services/socket";
import { stripAccents } from "../utils/stripAccents";
import { idsEqual } from "../services/idUtils";
import {
  getGroupeByRoomId,
  updateGroupeInfo,
  transferAdmin,
  addMembreToGroupe,
  removeMembreFromGroupe,
  toggleMuteMember,
} from "../services/groupes";
import { getUsers } from "../services/comptes-users";
import { FiEdit2 } from "react-icons/fi";
import { IoPersonAddSharp } from "react-icons/io5";
import { BsTrash } from "react-icons/bs";
import { HiPencilSquare } from "react-icons/hi2";
import { MdEditOff } from "react-icons/md";
import { IoMdExit } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import { FaShieldAlt } from "react-icons/fa";
import InputFile from "../components/InputFile";
import appLogo from "../assets/img/app_logo.png";

export default function GroupeDetailsPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;

  const [groupe, setGroupe] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edition
  const [editMode, setEditMode] = useState(false);
  const [editNom, setEditNom] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // Ajout membre
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]);

  // Transfer admin
  const [showTransferAdmin, setShowTransferAdmin] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);

  // Admin leave modal
  const [showAdminLeaveModal, setShowAdminLeaveModal] = useState(false);
  const [leaveTransferTarget, setLeaveTransferTarget] = useState(null);

  const isAdmin = groupe && idsEqual(groupe.groupeInfo?.adminId, user.userId);

  const fetchGroupe = useCallback(async () => {
    const result = await getGroupeByRoomId(roomId);
    if (result && result.status === 200) {
      setGroupe(result.groupe);
      setEditNom(result.groupe.groupeInfo?.nom || "");
    } else {
      navigate("/*");
      return;
    }
    setLoading(false);
  }, [roomId, navigate]);

  useEffect(() => {
    fetchGroupe();
  }, [fetchGroupe]);

  // Socket listeners
  useEffect(() => {
    const handleInfoUpdated = ({ groupe: g }) => {
      if (g.roomId === roomId) {
        setGroupe(g);
        setEditNom(g.groupeInfo?.nom || "");
      }
    };
    const handleMembreAdded = ({ groupe: g }) => {
      if (g.roomId === roomId) {
        setGroupe(g);
      }
    };
    const handleMembreRemoved = ({ groupe: g, removedMembreId }) => {
      if (g.roomId === roomId) {
        if (idsEqual(removedMembreId, user.userId)) {
          navigate("/groupes");
        } else {
          setGroupe(g);
        }
      }
    };
    const handleAdminTransferred = ({ groupe: g }) => {
      if (g.roomId === roomId) {
        setGroupe(g);
      }
    };

    const handleMembreMuted = ({ groupe: g }) => {
      if (g.roomId === roomId) {
        setGroupe(g);
      }
    };

    socket.on("groupe_info_updated", handleInfoUpdated);
    socket.on("membre_added_to_groupe", handleMembreAdded);
    socket.on("membre_removed_from_groupe", handleMembreRemoved);
    socket.on("admin_transferred", handleAdminTransferred);
    socket.on("membre_muted_in_groupe", handleMembreMuted);

    return () => {
      socket.off("groupe_info_updated", handleInfoUpdated);
      socket.off("membre_added_to_groupe", handleMembreAdded);
      socket.off("membre_removed_from_groupe", handleMembreRemoved);
      socket.off("admin_transferred", handleAdminTransferred);
      socket.off("membre_muted_in_groupe", handleMembreMuted);
    };
  }, [roomId, user.userId, navigate]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const handleSaveInfo = async () => {
    if (!groupe || loadingAction) return;
    setLoadingAction(true);
    const formData = new FormData();
    if (editNom && editNom !== groupe.groupeInfo.nom) {
      formData.append("nom", editNom);
    }
    if (editImage) {
      formData.append("image", editImage);
    }
    const result = await updateGroupeInfo(groupe.roomId, formData, workspaceId);
    if (result && result.status === 200) {
      setGroupe(result.groupe);
      socket.emit("groupe_info_updated", {
        roomId: groupe.roomId,
        groupe: result.groupe,
      });
      setEditMode(false);
      setEditImage(null);
      showSuccess("Groupe modifie avec succes.");
    } else {
      showSuccess(result?.erreur || "Erreur lors de la modification.");
    }
    setLoadingAction(false);
  };

  const handleAddMember = async (membreId) => {
    if (!groupe || loadingAction) return;
    setLoadingAction(true);
    const result = await addMembreToGroupe(groupe.roomId, membreId);
    if (result && result.status === 200) {
      setGroupe(result.groupe);
      socket.emit("membre_added_to_groupe", {
        roomId: groupe.roomId,
        groupe: result.groupe,
        newMembreId: membreId,
      });
      setShowAddMember(false);
      setSearchTerm("");
      showSuccess("Membre ajoute avec succes.");
    }
    setLoadingAction(false);
  };

  const handleRemoveMember = async (membreId, newAdminId) => {
    if (!groupe || loadingAction) return;
    const isSelf = idsEqual(membreId, user.userId);
    setLoadingAction(true);
    const result = await removeMembreFromGroupe(
      groupe.roomId,
      membreId,
      newAdminId || null
    );
    if (result && result.status === 200) {
      // Trouver les infos du membre retiré
      const leavingMember = (groupe.membres || []).find(
        (m) => (m.userId || m).toString() === membreId.toString()
      );
      socket.emit("membre_removed_from_groupe", {
        roomId: groupe.roomId,
        groupe: result.groupe,
        removedMembreId: membreId,
        leavingMember: leavingMember || { userId: membreId },
        isAdminAction: isAdmin && !isSelf,
        adminId: isAdmin && !isSelf ? user.userId : null,
        autoTransferredAdminId: result.autoTransferredAdminId || null,
      });
      if (isSelf) {
        navigate("/groupes");
      } else {
        setGroupe(result.groupe);
        showSuccess("Membre retire avec succes.");
      }
    }
    setLoadingAction(false);
  };

  // Admin quitte : ouvrir le modal si plus d'un membre restant
  const handleAdminLeave = () => {
    const otherMembers = (groupe.membres || []).filter(
      (m) => !idsEqual(m.userId, user.userId)
    );
    if (otherMembers.length === 0) {
      // Seul membre, quitter simplement
      handleRemoveMember(user.userId);
      return;
    }
    setLeaveTransferTarget(null);
    setShowAdminLeaveModal(true);
  };

  // Confirmer le départ de l'admin (avec ou sans choix de successeur)
  const handleConfirmAdminLeave = () => {
    setShowAdminLeaveModal(false);
    handleRemoveMember(user.userId, leaveTransferTarget);
  };

  const handleToggleMute = async (membreId) => {
    if (!groupe || loadingAction) return;
    setLoadingAction(true);
    const result = await toggleMuteMember(groupe.roomId, membreId);
    if (result && result.status === 200) {
      setGroupe(result.groupe);
      socket.emit("membre_muted_in_groupe", {
        roomId: groupe.roomId,
        groupe: result.groupe,
        membreId,
        isMuted: result.isMuted,
      });
      showSuccess(result.isMuted ? "Membre muté." : "Membre démuté.");
    }
    setLoadingAction(false);
  };

  const handleTransferAdmin = async () => {
    if (!transferTarget || loadingAction) return;
    if (
      !window.confirm(
        `Transférer le rôle d'administrateur à ce membre ? Vous perdrez vos droits d'admin.`
      )
    )
      return;
    setLoadingAction(true);
    const result = await transferAdmin(groupe.roomId, transferTarget);
    if (result && result.status === 200) {
      setGroupe(result.groupe);
      socket.emit("admin_transferred", {
        roomId: groupe.roomId,
        groupe: result.groupe,
      });
      setShowTransferAdmin(false);
      setTransferTarget(null);
      showSuccess("Admin transféré avec succès.");
    }
    setLoadingAction(false);
  };

  const handleShowAddMember = () => {
    setShowAddMember(true);
    getUsers(null, setAllUsers);
  };

  if (loading) return <Spinner visibility={true} />;

  const membresActuelsIds = (groupe.membres || []).map((m) =>
    (m.userId || m).toString()
  );
  const filteredUsers = allUsers.filter((u) => {
    if (membresActuelsIds.includes(u.userId || u._id?.toString())) return false;
    if (!searchTerm) return true;
    const term = stripAccents(searchTerm);
    return (
      stripAccents(u.prenom).includes(term) ||
      stripAccents(u.nom).includes(term) ||
      stripAccents(u.username).includes(term)
    );
  });

  const otherMembers = (groupe.membres || []).filter(
    (m) => !idsEqual(m.userId, user.userId)
  );

  return (
    <DisplayDiscussionForMiniScreenProvider>
      <Navbar />
      <ProfileSquelette>
        {/* En-tete groupe */}
        <div className="tyn-profile-head">
          <div className="tyn-profile-cover d-flex justify-content-center">
            <img
              className="tyn-profile-cover-image my-tyn-logo"
              src={appLogo}
              alt="app_logo"
            />
          </div>
          <div className="tyn-profile-info">
            <div className="tyn-media-group align-items-start">
              <div className="tyn-media tyn-media-bordered tyn-size-4xl tyn-profile-avatar">
                <Person
                  person={{
                    image: groupe.groupeInfo?.imageUrl || "",
                    nom: groupe.groupeInfo?.nom?.[1] || "",
                    prenom: groupe.groupeInfo?.nom || "",
                    userId: groupe._id?.toString(),
                    colors: groupe.groupeInfo?.color,
                  }}
                  animate="animate__animated animate__pulse animate__infinite"
                />
              </div>
              <div className="tyn-media-col">
                {editMode ? (
                  <div className="d-flex flex-column gap-2">
                    <input
                      type="text"
                      className="form-control"
                      value={editNom}
                      onChange={(e) => setEditNom(e.target.value)}
                      placeholder="Nom du groupe"
                    />
                    <InputFile
                      setImageGroupe={setEditImage}
                      currentImage={groupe.groupeInfo?.imageUrl || null}
                    />
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveInfo}
                        disabled={loadingAction}
                      >
                        Sauvegarder
                      </button>
                      <button
                        className="btn btn-light"
                        onClick={() => {
                          setEditMode(false);
                          setEditNom(groupe.groupeInfo?.nom || "");
                          setEditImage(null);
                        }}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="tyn-media-row">
                    <h2 className="name" style={{ fontSize: "15px" }}>
                      {groupe.groupeInfo?.nom}
                    </h2>
                    {isAdmin && (
                      <button
                        className="btn btn-icon btn-sm btn-light ms-2"
                        onClick={() => setEditMode(true)}
                        title="Modifier le nom et l'image"
                      >
                        <FiEdit2 />
                      </button>
                    )}
                  </div>
                )}
                <div className="tyn-media-row has-dot-sap">
                  <span className="content">
                    {groupe.membres?.length || 0} membres
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {successMsg && (
          <div className="alert alert-success mt-3">{successMsg}</div>
        )}

        {/* Actions rapides */}
        <div className="d-flex gap-2 flex-wrap mt-3 mb-3">
          <button
            className="btn btn-outline-primary"
            onClick={() => navigate("/groupes")}
          >
            Retour aux groupes
          </button>
          {isAdmin && (
            <>
              <button className="btn btn-primary" onClick={handleShowAddMember}>
                <IoPersonAddSharp className="me-1" />
                Ajouter un membre
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowTransferAdmin(!showTransferAdmin)}
              >
                <FaShieldAlt className="me-1" />
                Rendre administrateur
              </button>
            </>
          )}
          <button
            className="btn btn-outline-danger"
            onClick={() => {
              if (isAdmin) {
                handleAdminLeave();
              } else {
                if (
                  window.confirm("Voulez-vous vraiment quitter ce groupe ?")
                ) {
                  handleRemoveMember(user.userId);
                }
              }
            }}
            disabled={loadingAction}
          >
            <IoMdExit className="me-1" />
            Quitter le groupe
          </button>
        </div>

        {/* Ajout de membre */}
        {showAddMember && (
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Ajouter un membre</h6>
              <button
                className="btn btn-sm btn-light"
                onClick={() => {
                  setShowAddMember(false);
                  setSearchTerm("");
                }}
              >
                <RxCross2 />
              </button>
            </div>
            <div className="card-body">
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <ul
                className="list-group list-group-flush"
                style={{ maxHeight: "250px", overflowY: "auto" }}
              >
                {filteredUsers.length === 0 ? (
                  <li className="list-group-item text-muted text-center">
                    Aucun utilisateur trouvé
                  </li>
                ) : (
                  filteredUsers.map((u) => (
                    <li
                      key={u.userId || u._id}
                      className="list-group-item d-flex align-items-center justify-content-between py-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleAddMember(u.userId || u._id)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div className="tyn-media tyn-size-sm tyn-circle">
                          <Person
                            person={{
                              image: u.userImage || "",
                              nom: u.nom || "",
                              prenom: u.prenom || "",
                              colors: u.color,
                              userId: u.userId || u._id,
                            }}
                          />
                        </div>
                        <span>
                          {u.prenom} {u.nom}
                        </span>
                      </div>
                      <IoPersonAddSharp className="text-primary" />
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Transfert admin */}
        {showTransferAdmin && isAdmin && (
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Transférer le rôle d'administrateur</h6>
              <button
                className="btn btn-sm btn-light"
                onClick={() => {
                  setShowTransferAdmin(false);
                  setTransferTarget(null);
                }}
              >
                <RxCross2 />
              </button>
            </div>
            <div className="card-body">
              <p className="text-muted small mb-3">
                Sélectionnez le membre qui deviendra le nouvel administrateur du
                groupe. Vous perdrez vos droits d'admin.
              </p>
              <ul className="list-group list-group-flush">
                {otherMembers.map((membre) => (
                  <li
                    key={membre.userId}
                    className={`list-group-item d-flex align-items-center justify-content-between py-2 ${
                      idsEqual(transferTarget, membre.userId) ? "active" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setTransferTarget(membre.userId)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div className="tyn-media tyn-size-sm tyn-circle">
                        <Person
                          person={{
                            image: membre.userImage || "",
                            nom: membre.nom || "",
                            prenom: membre.prenom || "",
                            colors: membre.color,
                            userId: membre.userId,
                          }}
                        />
                      </div>
                      <span>
                        {membre.prenom} {membre.nom}
                      </span>
                    </div>
                    {idsEqual(transferTarget, membre.userId) && (
                      <FaShieldAlt className="text-warning" />
                    )}
                  </li>
                ))}
              </ul>
              {transferTarget && (
                <button
                  className="btn btn-warning mt-3 w-100"
                  onClick={handleTransferAdmin}
                  disabled={loadingAction}
                >
                  Confirmer le transfert
                </button>
              )}
            </div>
          </div>
        )}

        {/* Liste des membres */}
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">Membres ({groupe.membres?.length || 0})</h6>
          </div>
          <ul className="list-group list-group-flush">
            {(groupe.membres || []).map((membre) => (
              <li
                key={membre.userId}
                className="list-group-item d-flex align-items-center justify-content-between py-3"
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="tyn-media tyn-size-md tyn-circle">
                    <Person
                      person={{
                        image: membre.userImage || "",
                        nom: membre.nom || "",
                        prenom: membre.prenom || "",
                        colors: membre.color,
                        userId: membre.userId,
                      }}
                    />
                  </div>
                  <div>
                    <span className="fw-medium">
                      {membre.prenom} {membre.nom}
                    </span>
                    {membre.username && (
                      <span
                        className="text-muted ms-1"
                        style={{ fontSize: "13px" }}
                      >
                        {membre.username}
                      </span>
                    )}
                    {idsEqual(membre.userId, groupe.groupeInfo?.adminId) && (
                      <span
                        className="badge bg-primary ms-2"
                        style={{ fontSize: "10px" }}
                      >
                        Admin
                      </span>
                    )}
                    {idsEqual(membre.userId, user.userId) && (
                      <span
                        className="badge bg-secondary ms-1"
                        style={{ fontSize: "10px" }}
                      >
                        Vous
                      </span>
                    )}
                    {(groupe.mutedMembers || []).some((id) =>
                      idsEqual(id, membre.userId)
                    ) && (
                      <span
                        className="badge bg-warning text-dark ms-1"
                        style={{ fontSize: "10px" }}
                      >
                        Muté
                      </span>
                    )}
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {isAdmin && !idsEqual(membre.userId, user.userId) && (
                    <>
                      <button
                        className={`btn btn-icon btn-light ${(groupe.mutedMembers || []).some((id) => idsEqual(id, membre.userId)) ? "text-primary" : "text-danger"}`}
                        onClick={() => handleToggleMute(membre.userId)}
                        disabled={loadingAction}
                        title={
                          (groupe.mutedMembers || []).some((id) =>
                            idsEqual(id, membre.userId)
                          )
                            ? "Redonner le droit d'écriture"
                            : "Mettre en lecture seule"
                        }
                        style={{ fontSize: "1.25rem" }}
                      >
                        {(groupe.mutedMembers || []).some((id) =>
                          idsEqual(id, membre.userId)
                        ) ? (
                          <HiPencilSquare />
                        ) : (
                          <MdEditOff />
                        )}
                      </button>
                      <button
                        className="btn btn-icon btn-light text-danger"
                        onClick={() => {
                          if (
                            window.confirm(
                              "Voulez-vous vraiment retirer ce membre ?"
                            )
                          ) {
                            handleRemoveMember(membre.userId);
                          }
                        }}
                        disabled={loadingAction}
                        title="Retirer du groupe"
                        style={{ fontSize: "1.25rem" }}
                      >
                        <BsTrash />
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </ProfileSquelette>

      {/* Modal : Admin quitte le groupe */}
      {showAdminLeaveModal && (
        <>
          <div
            className="modal fade show animate__animated animate__backInDown"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0">
                <div className="modal-body p-4">
                  <h5 className="mb-3">Quitter le groupe</h5>
                  <p className="mb-3">
                    Vous êtes l'administrateur de ce groupe. Avant de quitter,
                    vous pouvez choisir qui deviendra le nouvel administrateur.
                  </p>
                  <p className="text-muted small mb-3">
                    Si vous ne choisissez personne, le système désignera
                    automatiquement le premier membre de la liste.
                  </p>
                  <ul
                    className="list-group list-group-flush"
                    style={{ maxHeight: "250px", overflowY: "auto" }}
                  >
                    {otherMembers.map((membre) => (
                      <li
                        key={membre.userId}
                        className={`list-group-item d-flex align-items-center justify-content-between py-2 ${
                          idsEqual(leaveTransferTarget, membre.userId)
                            ? "active"
                            : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setLeaveTransferTarget(membre.userId)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="tyn-media tyn-size-sm tyn-circle">
                            <Person
                              person={{
                                image: membre.userImage || "",
                                nom: membre.nom || "",
                                prenom: membre.prenom || "",
                                colors: membre.color,
                                userId: membre.userId,
                              }}
                            />
                          </div>
                          <span>
                            {membre.prenom} {membre.nom}
                          </span>
                        </div>
                        {idsEqual(leaveTransferTarget, membre.userId) && (
                          <FaShieldAlt className="text-warning" />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="modal-footer d-flex gap-2">
                  <button
                    className="btn btn-light"
                    onClick={() => setShowAdminLeaveModal(false)}
                  >
                    Annuler
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleConfirmAdminLeave}
                    disabled={loadingAction}
                  >
                    <IoMdExit className="me-1" />
                    {leaveTransferTarget
                      ? "Transférer et quitter"
                      : "Laisser le système décider et quitter"}
                  </button>
                </div>
                <button
                  className="btn bg-danger text-white btn-md btn-icon btn-pill btn-white shadow position-absolute top-0 end-0 mt-n3 me-n3"
                  onClick={() => setShowAdminLeaveModal(false)}
                >
                  <RxCross2 />
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </DisplayDiscussionForMiniScreenProvider>
  );
}
